import { X } from 'lucide-react';
import { Dialog as RD } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { IconButton } from './IconButton.js';
import './dialog.css';

export interface DrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactElement;
  title: ReactNode;
  description?: ReactNode;
  /** Header actions (left of the close button). */
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  /** sm 420, md 480, lg 560 px (BUILD-PLAN 3.3 peek drawer). */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Modal drawers dim and block the page. Non-modal drawers (peek) leave the
   * page interactive. Either way Esc closes and focus returns to the opener.
   */
  modal?: boolean;
  /** Called before closing on Esc; return false to keep it open (dirty panels nudge). */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  className?: string;
}

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Opening focuses the first field in the body (falling back to the drawer
 * itself) rather than the Close button: focusing Close opened its "Esc"
 * tooltip, a dismissable layer that swallowed the first Esc.
 */
function focusBody(event: Event): void {
  const content = event.currentTarget;
  if (!(content instanceof HTMLElement)) return;
  event.preventDefault();
  const body = content.querySelector<HTMLElement>('.vel-drawer__body');
  const first = body?.querySelector<HTMLElement>(FOCUSABLE);
  (first ?? content).focus({ preventScroll: true });
}

export function Drawer({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  actions,
  children,
  footer,
  side = 'right',
  size = 'md',
  modal = true,
  onEscapeKeyDown,
  className,
}: DrawerProps) {
  return (
    <RD.Root
      modal={modal}
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange ? { onOpenChange } : {})}
    >
      {trigger ? <RD.Trigger asChild>{trigger}</RD.Trigger> : null}
      <RD.Portal>
        {modal ? <RD.Overlay className="vel-drawer-overlay" /> : null}
        <RD.Content
          className={cx('vel-drawer', className)}
          data-side={side}
          data-size={size}
          {...(description ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={focusBody}
          {...(onEscapeKeyDown ? { onEscapeKeyDown } : {})}
          {...(modal ? {} : { onInteractOutside: (e: Event) => e.preventDefault() })}
        >
          <header className="vel-drawer__header">
            <div className="vel-drawer__titles">
              <RD.Title className="vel-drawer__title">{title}</RD.Title>
              {description ? (
                <RD.Description className="vel-drawer__desc">{description}</RD.Description>
              ) : null}
            </div>
            {actions}
            <RD.Close asChild>
              <IconButton label="Close" shortcut={['Esc']} icon={<X size={16} />} size="sm" />
            </RD.Close>
          </header>
          <div className="vel-drawer__body">{children}</div>
          {footer ? <footer className="vel-drawer__footer">{footer}</footer> : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
